// Préstamos IESS (quirografarios/hipotecarios): retención mensual obligatoria
// que el motor del rol descuenta automáticamente. Nómina → IESS y Tributario.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Loader2, Pencil, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, Modal, fecha, fieldCls, money, nomRpc, useDialogoEnLinea, type EmpleadoRow, type PrestamoIessRow, hoyISO } from './shared';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { fechaLocal } from '@/lib/utils/fecha';

const vacio = { id: '', empleado_id: '', tipo: 'quirografario', cuota_mensual: '', fecha_inicio: hoyISO(), observacion: '' };

export function PrestamosIess() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [estado, setEstado] = useState('activo');
  const { data: rows = [], isFetching } = useQuery<PrestamoIessRow[]>({
    queryKey: ['nom-prestamos-iess', estado],
    queryFn: () => nomRpc<PrestamoIessRow[]>('nom_prestamos_iess', { _estado: estado }),
  });
  const { data: empleados = [] } = useQuery<EmpleadoRow[]>({
    queryKey: ['nom-empleados-opt-act'],
    queryFn: () => nomRpc<EmpleadoRow[]>('nom_empleados', { _q: null, _solo_activos: true }),
  });

  const [abierto, setAbierto] = useState(false);
  const [form, setForm] = useState<typeof vacio>(vacio);

  const abrir = (p?: PrestamoIessRow) => {
    if (p) setForm({ id: p.id, empleado_id: p.empleado_id, tipo: p.tipo, cuota_mensual: String(p.cuota_mensual), fecha_inicio: p.fecha_inicio.slice(0, 10), observacion: p.observacion ?? '' });
    else setForm(vacio);
    setAbierto(true);
  };
  const guardar = useMutation({
    mutationFn: () => nomRpc('nom_prestamo_iess_guardar', { _data: { ...form, id: form.id || null, cuota_mensual: Number(form.cuota_mensual), observacion: form.observacion || null } }),
    onSuccess: () => { toast.success('Préstamo IESS guardado'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['nom-prestamos-iess'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const terminar = useMutation({
    mutationFn: (id: string) => nomRpc('nom_prestamo_iess_terminar', { _id: id }),
    onSuccess: () => { toast.success('Préstamo marcado como terminado'); void qc.invalidateQueries({ queryKey: ['nom-prestamos-iess'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const totalCuotas = rows.filter((r) => r.estado === 'activo').reduce((s, r) => s + Number(r.cuota_mensual), 0);
  const totalCuotasExp = rows.reduce((s, r) => s + Number(r.cuota_mensual), 0);

  const headersExp = ['N°', 'Empleado', 'Tipo', 'Inicio', 'Cuota mensual', 'Observación', 'Estado'];
  const MONEYCOLS = [4];
  const filasExport: Celda[][] = [
    ...rows.map((p, i): Celda[] => [
      i + 1, p.razon_social, p.tipo, fecha(p.fecha_inicio), Number(p.cuota_mensual), p.observacion ?? '', p.estado,
    ]),
    ['', '', '', 'TOTAL', totalCuotasExp, '', ''],
  ];

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Préstamos IESS</h1>
        <div className="flex items-center gap-2">
          <ExportarBotones
            nombre={`prestamos-iess-${fechaLocal()}`} titulo="Préstamos IESS"
            headers={headersExp} filas={filasExport} colsDerecha={[0, 4]} disabled={rows.length === 0}
            fmtPDF={(v, c) => (MONEYCOLS.includes(c) ? (v === '' ? '' : money(Number(v))) : String(v ?? ''))}
          />
          <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Registrar préstamo</Button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Retención por orden del IESS (quirografario/hipotecario): la cuota se descuenta íntegra en cada rol
        mientras el préstamo esté activo. Márcalo terminado cuando el IESS deje de reportarlo.
      </p>

      <select className={`${fieldCls} w-56`} value={estado} onChange={(e) => setEstado(e.target.value)} aria-label="Estado">
        <option value="activo">Activos</option>
        <option value="terminado">Terminados</option>
        <option value="todos">Todos</option>
      </select>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Préstamos ({rows.length}) — cuota mensual activa {money(totalCuotas)}</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Empleado</th><th className="py-2 pr-3">Tipo</th><th className="py-2 pr-3">Inicio</th>
              <th className="py-2 pr-3 text-right">Cuota mensual</th><th className="py-2 pr-3">Observación</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin préstamos IESS</td></tr>}
              {rows.map((p) => (
                <tr key={p.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3">{p.razon_social}<div className="text-xs text-muted-foreground font-mono">{p.identificacion}</div></td>
                  <td className="py-2 pr-3 capitalize">{p.tipo}</td>
                  <td className="py-2 pr-3 font-mono">{fecha(p.fecha_inicio)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums font-semibold">{money(p.cuota_mensual)}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{p.observacion}</td>
                  <td className="py-2 pr-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${p.estado === 'activo' ? 'bg-pendiente-soft text-pendiente' : 'bg-muted text-muted-foreground'}`}>{p.estado}</span>
                  </td>
                  <td className="py-2 text-right whitespace-nowrap">
                    {p.estado === 'activo' && (
                      <>
                        <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(p)}><Pencil className="w-4 h-4" /></button>
                        <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-estable hover:bg-estable-soft ml-1" title="Marcar terminado" onClick={async () => { if (await dlg.confirmar('¿Marcar como terminado?', { detalle: 'Dejará de descontarse en el rol.', aceptar: 'Marcar terminado' })) terminar.mutate(p.id); }}><CheckCircle2 className="w-4 h-4" /></button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={form.id ? 'Editar préstamo IESS' : 'Registrar préstamo IESS'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <Campo label="Empleado *">
              <select className={fieldCls} value={form.empleado_id} onChange={(e) => setForm({ ...form, empleado_id: e.target.value })} disabled={!!form.id}>
                <option value="">— Selecciona —</option>
                {empleados.map((e) => <option key={e.id} value={e.id}>{e.razon_social} ({e.identificacion})</option>)}
              </select>
            </Campo>
            <div className="grid grid-cols-3 gap-3">
              <Campo label="Tipo *">
                <select className={fieldCls} value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
                  <option value="quirografario">Quirografario</option>
                  <option value="hipotecario">Hipotecario</option>
                </select>
              </Campo>
              <Campo label="Cuota mensual ($) *"><input type="number" min="0" step="0.01" className={fieldCls} value={form.cuota_mensual} onChange={(e) => setForm({ ...form, cuota_mensual: e.target.value })} /></Campo>
              <Campo label="Inicio de descuento *"><input type="date" className={fieldCls} value={form.fecha_inicio} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })} /></Campo>
            </div>
            <Campo label="Observación"><input className={fieldCls} value={form.observacion} onChange={(e) => setForm({ ...form, observacion: e.target.value })} placeholder="N.º de préstamo IESS…" /></Campo>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.empleado_id || !Number(form.cuota_mensual)} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
